import type { HoverIdleResetAutomationState, NHovBranchId } from "../types";

export type ResetAutomationMinimumGameplayIntervalFamily =
  | "mHov"
  | "nHov"
  | "oHov";

export type ResetAutomationMinimumGameplayIntervalCarryMs =
  HoverIdleResetAutomationState["minimumGameplayIntervalCarryMs"];

export const HIDDEN_RESET_AUTOMATION_INITIAL_MINIMUM_GAMEPLAY_INTERVAL_MS = 50;

const RESET_AUTOMATION_MINIMUM_GAMEPLAY_INTERVAL_MS_BY_FAMILY: Readonly<
  Record<ResetAutomationMinimumGameplayIntervalFamily, number>
> = {
  mHov: HIDDEN_RESET_AUTOMATION_INITIAL_MINIMUM_GAMEPLAY_INTERVAL_MS,
  nHov: HIDDEN_RESET_AUTOMATION_INITIAL_MINIMUM_GAMEPLAY_INTERVAL_MS,
  oHov: HIDDEN_RESET_AUTOMATION_INITIAL_MINIMUM_GAMEPLAY_INTERVAL_MS,
};

export function createDefaultResetAutomationState(): HoverIdleResetAutomationState {
  return {
    minimumGameplayIntervalCarryMs: {
      mHov: 0,
      nHov: 0,
      oHov: 0,
    },
  };
}

export function normalizeResetAutomationState(
  state: HoverIdleResetAutomationState | undefined,
): HoverIdleResetAutomationState {
  return {
    minimumGameplayIntervalCarryMs: {
      mHov: normalizeCarryMs(state?.minimumGameplayIntervalCarryMs?.mHov),
      nHov: normalizeCarryMs(state?.minimumGameplayIntervalCarryMs?.nHov),
      oHov: normalizeCarryMs(state?.minimumGameplayIntervalCarryMs?.oHov),
    },
  };
}

export function cloneResetAutomationState(
  state: HoverIdleResetAutomationState,
): HoverIdleResetAutomationState {
  return {
    minimumGameplayIntervalCarryMs: {
      mHov: state.minimumGameplayIntervalCarryMs.mHov,
      nHov: state.minimumGameplayIntervalCarryMs.nHov,
      oHov: state.minimumGameplayIntervalCarryMs.oHov,
    },
  };
}

export function advanceResetAutomationMinimumGameplayIntervalCarry(
  carry: ResetAutomationMinimumGameplayIntervalCarryMs,
  elapsedMs: number,
): ResetAutomationMinimumGameplayIntervalCarryMs {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    return carry;
  }
  return {
    mHov: carry.mHov + elapsedMs,
    nHov: carry.nHov + elapsedMs,
    oHov: carry.oHov + elapsedMs,
  };
}

export function consumeResetAutomationMinimumGameplayInterval(
  carry: ResetAutomationMinimumGameplayIntervalCarryMs,
  family: ResetAutomationMinimumGameplayIntervalFamily,
  nHovBranchId?: NHovBranchId,
): ResetAutomationMinimumGameplayIntervalCarryMs {
  const minimumGameplayIntervalMs =
    resolveResetAutomationMinimumGameplayIntervalMs(family, nHovBranchId);
  if (minimumGameplayIntervalMs <= 0) {
    return carry;
  }
  return {
    ...carry,
    [family]: Math.max(0, carry[family] - minimumGameplayIntervalMs),
  };
}

export function isResetAutomationMinimumGameplayIntervalSatisfied(
  carry: ResetAutomationMinimumGameplayIntervalCarryMs,
  family: ResetAutomationMinimumGameplayIntervalFamily,
  elapsedOffsetMs = 0,
  nHovBranchId?: NHovBranchId,
): boolean {
  const minimumGameplayIntervalMs =
    resolveResetAutomationMinimumGameplayIntervalMs(family, nHovBranchId);
  if (minimumGameplayIntervalMs <= 0) {
    return true;
  }
  return (
    carry[family] + Math.max(0, elapsedOffsetMs) >= minimumGameplayIntervalMs
  );
}

export function resolveResetAutomationMinimumGameplayIntervalRemainingMs(
  carry: ResetAutomationMinimumGameplayIntervalCarryMs,
  family: ResetAutomationMinimumGameplayIntervalFamily,
  nHovBranchId?: NHovBranchId,
): number {
  const minimumGameplayIntervalMs =
    resolveResetAutomationMinimumGameplayIntervalMs(family, nHovBranchId);
  if (minimumGameplayIntervalMs <= 0) {
    return 0;
  }
  return Math.max(0, minimumGameplayIntervalMs - carry[family]);
}

export function resolveResetAutomationMinimumGameplayIntervalMs(
  family: ResetAutomationMinimumGameplayIntervalFamily,
  _nHovBranchId?: NHovBranchId,
): number {
  return RESET_AUTOMATION_MINIMUM_GAMEPLAY_INTERVAL_MS_BY_FAMILY[family];
}

function normalizeCarryMs(value: number | undefined): number {
  if (!Number.isFinite(value) || value === undefined || value < 0) {
    return 0;
  }
  return value;
}
